import { useCallback, useEffect, useRef } from "react";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { DocumentsTaskAskButton } from "../../DocumentsTaskAskButton";

interface Props {
    selectedCount: number;
    canRemoveFromTask: boolean;
    canAskAbout?: boolean;
    isRemoving: boolean;
    onClearSelection: () => void;
    onRemoveFromTask: () => void;
    onAskAbout?: () => void;
}

export function GetDocumentsBulkActions({
    selectedCount,
    canRemoveFromTask,
    canAskAbout,
    isRemoving,
    onClearSelection,
    onRemoveFromTask,
    onAskAbout,
}: Readonly<Props>) {
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const { open, registerOpenOverride, unregisterOpenOverride } =
        useCommands();

    const hasBulkActions = canRemoveFromTask || canAskAbout;

    const handleOpenBulkActions = useCallback(() => {
        if (!hasBulkActions) {
            return;
        }
        open(CommandsDirectory.GET_DOCUMENTS_BULK, {
            selectedGetDocumentsCount: selectedCount,
            onGetDocumentsBulkRemoveFromTask: canRemoveFromTask
                ? () => {
                      if (refuseIfViewOnly() || isRemoving) {
                          return;
                      }
                      onRemoveFromTask();
                  }
                : undefined,
            onGetDocumentsBulkAskAbout:
                canAskAbout && onAskAbout ? onAskAbout : undefined,
        });
    }, [
        canAskAbout,
        canRemoveFromTask,
        hasBulkActions,
        isRemoving,
        onAskAbout,
        onRemoveFromTask,
        open,
        refuseIfViewOnly,
        selectedCount,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (!hasBulkActions || isViewOnly) {
            unregisterOpenOverride();
            return;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [
        hasBulkActions,
        isViewOnly,
        registerOpenOverride,
        unregisterOpenOverride,
    ]);

    return (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                <Tooltip content={`Deselect ${selectedCount}`}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={onClearSelection}
                    >
                        {selectedCount} selected
                    </Button>
                </Tooltip>
            </HStack>
            {hasBulkActions && (
                <HStack gap={8}>
                    {canAskAbout && onAskAbout && (
                        <DocumentsTaskAskButton
                            selectedCount={selectedCount}
                            onAskAbout={onAskAbout}
                        />
                    )}
                    <Tooltip content="Open bulk actions">
                        <Button
                            variant="primary"
                            size="md"
                            disabled={isViewOnly}
                            onClick={handleOpenBulkActions}
                            rightIcon={
                                <span data-slot-box="">
                                    <Shortcut keys="mod+k" />
                                </span>
                            }
                        >
                            Actions
                        </Button>
                    </Tooltip>
                </HStack>
            )}
        </CommandIsland>
    );
}
